import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const Stat = ({ label, value, testId }) => (
  <div className="brut p-3" data-testid={testId}>
    <div className="font-mono uppercase text-[10px] tracking-widest opacity-70">{label}</div>
    <div className="font-display text-2xl">{value ?? "—"}</div>
  </div>
);

export default function PriceRefreshPanel() {
  const [s, setS] = useState(null);
  const load = () => api.get("/admin/price-refresh/status").then((r) => setS(r.data));
  const busy = ["queued", "running"].includes(s?.last?.status);

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [busy]);

  const run = async () => {
    try {
      await api.post("/admin/price-refresh/run");
      toast.success("Price refresh started");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start refresh");
    }
  };

  const last = s?.last;
  return (
    <div className="brut bg-white p-5 space-y-4" data-testid="price-refresh-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-2xl tracking-tight">eBay price & stock refresh</h2>
        <span className="font-mono uppercase text-[10px] tracking-widest opacity-60">Runs nightly · 3am Eastern</span>
        <button onClick={run} disabled={busy} data-testid="price-refresh-run" className="ml-auto brut pcs-bg-blue text-white px-3 py-1.5 font-bold uppercase text-xs brut-press disabled:opacity-50 inline-flex items-center gap-1">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} strokeWidth={3} /> {busy ? "Refreshing..." : "Run now"}
        </button>
      </div>
      <div className="text-sm" data-testid="price-refresh-last">
        {!s ? "Loading..." : last ? (
          <>Last run: <b>{last.status}</b> ({last.trigger}) · {new Date(last.finished_at || last.created_at).toLocaleString()}</>
        ) : "No refresh has run yet."}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Checked" value={last?.checked} testId="price-refresh-checked" />
        <Stat label="Price changed" value={last?.price_changed} testId="price-refresh-changed" />
        <Stat label="Unavailable" value={last?.unavailable} testId="price-refresh-unavailable" />
        <Stat label="Back in stock" value={last?.restored} testId="price-refresh-restored" />
        <Stat label="Errors" value={last?.errors} testId="price-refresh-errors" />
      </div>
      <p className="text-xs opacity-70">
        {s?.unavailable ?? 0} of {s?.ebay_products ?? 0} eBay products are currently hidden from the storefront because they're sold out or ended. They reappear automatically if eBay lists them again.
      </p>
    </div>
  );
}
